import React, {useState, useEffect} from 'react';
import {Block, Title, Select, Icon, FormGroup, useForm, BoolSelect, Yup} from '../../../components/components';
import { useTranslation } from 'react-i18next';
import { Button, Form, Col, Row, ListGroup } from 'react-bootstrap';
import { useGetPrivacyQuery, useUpdatePrivacyMutation } from "../../../api/privacyApiSlice";
import { useLazyGetVendorQuery, useLazyGetVendorsQuery } from "../../../api/vendorsApiSlice";
import { PurposeListItem } from '../../../components/PurposeListItem';
import { useLazyGetPurposesQuery } from '../../../api/purposesApiSlice';

function VendorSelect({editable, formData, setFormData}) {
  const [getVendors] = useLazyGetVendorsQuery();
  const [getVendor] = useLazyGetVendorQuery()
  const loadOptions = ( inputValue, callback ) => {
    getVendors({search: inputValue}, true).then((response) => {
      const options = response.data.map(formatVendorOption)
      callback(options)
    })
  }
  const handleOnChange = (e) => {
    if (e) {
      getVendor(e, true).then((resp) => {
        setFormData(resp.data)
      })
    }
  }
  const formatVendorOption = (vendor) => {
		if (vendor.vendor_id) {
      return {
        value: vendor.vendor_id,
        label: `[${vendor.iab_id}] ${vendor.name}`
      }
    } else {
			return vendor.vendor_id
		}
	}

  useEffect(() => {
    if(formData.vendor_id) {
      getVendor(formData.vendor_id, true).then((resp)=>{
        setFormData(resp.data)
      })
    }
  }, [formData.vendor_id])

  return (
      <Select
        isDisabled={!editable}
        loadOptions={loadOptions}
        formatOption={formatVendorOption}
        onChange={handleOnChange}
        value={formatVendorOption(formData)}
      >
        <Option>Please enter something</Option>
      </Select>
  )
}

function PurposesBlock({purposes=[], special_features=[]}) {
	const { t, i18n } = useTranslation();
  return (
    <>
    {(purposes.length > 0 || special_features.length > 0) &&
      <Block as={Col} md={6} title={t("privacy.purposes")}>
        <ListGroup>
          {purposes.map ( (purpose, index) => <PurposeListItem key={`purpose-${index}`} type="purpose" object={purpose} />)}
          {special_features.map ( (special_feature, index) => <PurposeListItem key={`special_feature-${index}`} type="specialFeature" object={special_feature} />)}
        </ListGroup>
      </Block>
    }
    </>
  )
}

function Privacy() {
	const { t, i18n } = useTranslation();
	const [editable, setEditable] = useState(false);
  const { data: privacy, isSuccess: isPrivacy, refetch } = useGetPrivacyQuery();
	const {formData, setFormData, getInputProps, formProps, submitButtonProps} = useForm({
		defaultObject: null,
		initialValues: {
			vendor_id: '',
    	name: '',
    	iab_id: '',
      is_iab: (obj) => obj ? !!obj.iab_id : '',
      policy_url: '',
      geomedia: '',
      geodata: '',
      purposes: [],
      special_features: []
		},
    validationSchema: Yup.object().shape({
      is_iab: Yup.bool().required(),
      policy_url: Yup.string().url().required(),
      geomedia: Yup.mixed().when('is_iab', {
        is: false,
        then: Yup.bool().required(),
      }),
      geodata: Yup.mixed().when('is_iab', {
        is: false,
        then: Yup.bool().required(),
      }),
      vendor_id: Yup.mixed().when('is_iab', {
        is: true,
        then: Yup.number().required(),
      }),
    }),
    onSubmit: handleSubmit,
    disabled: !editable
	})

  function prepareFormData(data) {
  	if (data.vendor_id && !!formData.iab_id) {
  		return (({vendor_id}) => ({vendor_id}))(data)
  	} else {
  		let res = (({policy_url}) => ({policy_url}))(data)
  		res.purpose_ids = data.purposes.map( p => p.id )
  		res.special_feature_ids = data.special_features.map( spf => spf.id )
  		return res;
  	}
  }

  function handleEdit() {
  	setEditable(true);
  }

  const [updatePrivacy] = useUpdatePrivacyMutation();
  function handleSubmit(data) {
    updatePrivacy(prepareFormData(data))
      .then(response => {
        setFormData(response.data)
        setEditable(false);
      })
      .catch(error => {
        console.log(error);
        setEditable(false);
        setFormData(privacy)
      });
  	setEditable(false);
  }

  function handleCancel() {
  	setEditable(false);
    setFormData(privacy)
  }

  useEffect(() => {
    if(isPrivacy) {
	    setFormData(privacy)
    }
  }, [isPrivacy])

  const [ getPurposes ] = useLazyGetPurposesQuery()
  useEffect(() => {
  	if ( !formData.is_iab && (typeof(formData.geomedia) == 'boolean' || typeof(formData.geodata) == 'boolean')) {
  		getPurposes({geomedia: formData.geomedia === '' ? false : formData.geomedia, geodata: formData.geodata === '' ? false : formData.geodata}, true)
        .then(response => {
          getInputProps('purposes').onChange(response.data.purposes)
          getInputProps('special_features').onChange(response.data.special_features)
        })
        .catch(error => {
          console.log(error);
        });
  	}
  }, [formData.geomedia, formData.geodata]);

	return (
    <Form {...formProps}>
      <Title main={t("menu.organisation")} sub={t("menu.privacy")} group={false}>
        {!editable && <Button variant='primary' onClick={handleEdit}>
          <Icon icon='edit' text={t("edit")} />
        </Button>}
        {editable && <Button {...submitButtonProps}>
          <Icon icon='save' text={t("save")} />
        </Button>}
        {editable && <Button variant='secondary' onClick={handleCancel}>
          <Icon icon='cancel' text={t("cancel")} />
        </Button>}
      </Title>
      <Row>
        <Block as={Col} md={6} title={t("privacy.general")}>
          <fieldset disabled={!editable}>

            <FormGroup as={Col} label={t("privacy.isIab")} {...getInputProps("is_iab")}>
              <BoolSelect isDisabled={!editable} />
            </FormGroup>

            {formData.is_iab && <FormGroup as={Col} label={t("privacy.selectVendor")} {...getInputProps("vendor_id")}>
              <VendorSelect editable={editable} formData={formData} setFormData={setFormData}/>
            </FormGroup>}

            {typeof(formData.is_iab) == "boolean" && <FormGroup as={Col} label={t("privacy.privacyPolicy")} {...getInputProps("policy_url")}>
              <Form.Control type="text" readOnly={formData.is_iab} />
            </FormGroup>}

            {formData.is_iab === false && <FormGroup
              as={Col}
              // md="6"
              label={t("privacy.geomedia")}
              input={BoolSelect}
              {...getInputProps("geomedia")}/>}

            {formData.is_iab === false && <FormGroup as={Col} label={t("privacy.geodata")} {...getInputProps("geodata")}>
              <BoolSelect isDisabled={!editable} />
            </FormGroup>}

          </fieldset>
        </Block>
        <PurposesBlock
          purposes={formData.purposes}
          special_features={formData.special_features}/>
      </Row>
    </Form>
	)
}

export default Privacy
